import React, { useEffect, useState } from 'react';
import { taskTemplateAPI, projectAPI, userAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2, FiEdit2, FiX, FiCheck, FiPlay, FiList, FiUser } from 'react-icons/fi';

const PRIORITY_COLORS = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };

const emptyTask  = () => ({ title:'', priority:'medium', dueDays:'', checklist:[] });
const emptyTmpl  = () => ({ name:'', description:'', tasks:[] });

export default function TaskTemplates() {
  const [templates, setTemplates] = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Create/edit modal
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);   // null = new
  const [form,     setForm]     = useState(emptyTmpl());
  const [saving,   setSaving]   = useState(false);

  // Apply modal
  const [applyModal,       setApplyModal]       = useState(null);  // template object
  const [applyProjId,      setApplyProjId]       = useState('');
  const [applying,         setApplying]          = useState(false);
  const [members,          setMembers]           = useState([]);   // team members for assignment
  const [defaultAssignee,  setDefaultAssignee]   = useState('');   // userId applied to all tasks
  const [taskAssignees,    setTaskAssignees]      = useState({});   // { taskIndex: userId } overrides

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tR, pR, uR] = await Promise.all([taskTemplateAPI.getAll(), projectAPI.getAll(), userAPI.getAll()]);
      setTemplates(tR.data.templates || []);
      setProjects((pR.data.projects || []).filter(p => p.status !== 'completed'));
      setMembers((uR.data.users || []).filter(u => u.role === 'team' || u.role === 'manager'));
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  /* ── Template form helpers ── */
  const openNew = () => { setEditing(null); setForm(emptyTmpl()); setModal(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name:t.name, description:t.description||'', tasks: JSON.parse(JSON.stringify(t.tasks||[])) }); setModal(true); };

  const addTaskRow = () => setForm(f => ({ ...f, tasks: [...f.tasks, emptyTask()] }));
  const removeTaskRow = (i) => setForm(f => ({ ...f, tasks: f.tasks.filter((_,idx)=>idx!==i) }));
  const updateTask = (i, field, val) => setForm(f => { const t=[...f.tasks]; t[i]={...t[i],[field]:val}; return {...f,tasks:t}; });

  const addCheckItem = (ti) => setForm(f => { const t=[...f.tasks]; t[ti]={...t[ti],checklist:[...(t[ti].checklist||[]),{text:''}]}; return {...f,tasks:t}; });
  const removeCheckItem = (ti,ci) => setForm(f => { const t=[...f.tasks]; t[ti]={...t[ti],checklist:t[ti].checklist.filter((_,idx)=>idx!==ci)}; return {...f,tasks:t}; });
  const updateCheckItem = (ti,ci,val) => setForm(f => { const t=[...f.tasks]; const cl=[...t[ti].checklist]; cl[ci]={...cl[ci],text:val}; t[ti]={...t[ti],checklist:cl}; return {...f,tasks:t}; });

  const save = async () => {
    if (!form.name.trim()) return toast.error('Template name required');
    setSaving(true);
    try {
      if (editing) {
        await taskTemplateAPI.update(editing.id, form);
        toast.success('Template updated');
      } else {
        await taskTemplateAPI.create(form);
        toast.success('Template created');
      }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await taskTemplateAPI.remove(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const openApplyModal = (t) => {
    setApplyModal(t);
    setApplyProjId('');
    setDefaultAssignee('');
    setTaskAssignees({});
  };

  const applyTemplate = async () => {
    if (!applyProjId) return toast.error('Select a project');
    setApplying(true);
    try {
      // Build per-task assignee map: fall back to defaultAssignee if no override
      const assignees = {};
      (applyModal.tasks || []).forEach((_, i) => {
        const uid = taskAssignees[i] || defaultAssignee;
        if (uid) assignees[i] = uid;
      });
      const r = await taskTemplateAPI.apply(applyModal.id, applyProjId, { defaultAssigneeId: defaultAssignee || null, assignees });
      toast.success(r.data.message);
      setApplyModal(null);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setApplying(false);
  };

  const S = {
    page: { maxWidth:1200, margin:'0 auto', fontFamily:"'Sora',sans-serif" },
    card: { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9' },
    btn:  (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff' }
        : v==='danger'  ? { background:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5' }
        : v==='green'   ? { background:'#dcfce7', color:'#16a34a', border:'1px solid #86efac' }
        :                 { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' }) }),
    inp:  { width:'100%', padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'Sora',sans-serif" },
    overlay: { position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
    modal:   { background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:680, maxHeight:'90vh', overflowY:'auto' },
    smModal: { background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:600, maxHeight:'90vh', overflowY:'auto' },
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:'#0f172a' }}>📋 Task Templates</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:14 }}>Define reusable task lists with checklists — apply to any project in one click</p>
        </div>
        <button style={S.btn('primary')} onClick={openNew}><FiPlus size={15}/> New Template</button>
      </div>

      {/* Empty state */}
      {templates.length === 0 && (
        <div style={{ ...S.card, textAlign:'center', padding:'56px 32px' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📋</div>
          <h3 style={{ margin:'0 0 8px', color:'#0f172a', fontSize:18, fontWeight:700 }}>No task templates yet</h3>
          <p style={{ color:'#64748b', fontSize:14, margin:'0 0 24px' }}>Create a template once, apply to any project. Tasks and checklists included.</p>
          <button style={S.btn('primary')} onClick={openNew}><FiPlus size={15}/> Create First Template</button>
        </div>
      )}

      {/* Template cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:16 }}>
        {templates.map(t => (
          <div key={t.id} style={{ ...S.card, transition:'all .2s' }}
            onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
            onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:700, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.name}</h3>
                {t.description && <p style={{ margin:0, fontSize:12, color:'#64748b', lineHeight:1.5 }}>{t.description}</p>}
              </div>
              <div style={{ display:'flex', gap:6, marginLeft:12, flexShrink:0 }}>
                <button style={{ ...S.btn(), padding:'6px 10px' }} onClick={()=>openEdit(t)} title="Edit"><FiEdit2 size={14}/></button>
                <button style={{ ...S.btn('danger'), padding:'6px 10px' }} onClick={()=>remove(t.id)} title="Delete"><FiTrash2 size={14}/></button>
              </div>
            </div>

            {/* Task preview */}
            <div style={{ marginBottom:16 }}>
              {(t.tasks||[]).length === 0 && <span style={{ fontSize:12, color:'#94a3b8' }}>No tasks defined</span>}
              {(t.tasks||[]).slice(0,4).map((task,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:PRIORITY_COLORS[task.priority]||'#94a3b8', flexShrink:0 }}/>
                  <span style={{ fontSize:13, color:'#0f172a', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.title}</span>
                  {task.checklist?.length > 0 && (
                    <span style={{ fontSize:11, color:'#6366f1', fontWeight:600, background:'#eef2ff', padding:'2px 7px', borderRadius:6 }}>
                      ☑ {task.checklist.length}
                    </span>
                  )}
                </div>
              ))}
              {(t.tasks||[]).length > 4 && (
                <div style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>+{(t.tasks||[]).length - 4} more tasks</div>
              )}
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:12, color:'#94a3b8' }}><FiList size={12} style={{ marginRight:4 }}/>{(t.tasks||[]).length} task{(t.tasks||[]).length!==1?'s':''}</span>
              <button style={S.btn('green')} onClick={() => openApplyModal(t)}>
                <FiPlay size={13}/> Apply to Project
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Create/Edit Modal ── */}
      {modal && (
        <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget)setModal(false)}}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:'#0f172a' }}>{editing ? 'Edit Template' : 'New Task Template'}</h2>
              <button style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }} onClick={()=>setModal(false)}><FiX size={20}/></button>
            </div>

            {/* Name + description */}
            <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block', marginBottom:4 }}>Template Name *</label>
            <input style={{ ...S.inp, marginBottom:12 }} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Website Development"/>
            <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block', marginBottom:4 }}>Description</label>
            <input style={{ ...S.inp, marginBottom:20 }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Optional description"/>

            {/* Tasks */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <label style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>Tasks ({form.tasks.length})</label>
              <button style={S.btn()} onClick={addTaskRow}><FiPlus size={13}/> Add Task</button>
            </div>

            {form.tasks.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 90px auto', gap:8, padding:'0 14px 6px', fontSize:11, fontWeight:700, color:'#94a3b8' }}>
                <span>TITLE</span><span>PRIORITY</span><span title="Due in X days from project start date">DUE (DAYS)</span><span/>
              </div>
            )}
            {form.tasks.length === 0 && (
              <div style={{ textAlign:'center', padding:'20px', color:'#94a3b8', fontSize:13, background:'#f8fafc', borderRadius:10, marginBottom:12 }}>
                No tasks yet — click "Add Task" above
              </div>
            )}

            {form.tasks.map((task, ti) => (
              <div key={ti} style={{ background:'#f8fafc', borderRadius:12, padding:14, marginBottom:10, border:'1px solid #f1f5f9' }}>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input style={{ ...S.inp, flex:1 }} value={task.title} onChange={e=>updateTask(ti,'title',e.target.value)} placeholder={`Task ${ti+1} title`}/>
                  <select style={{ ...S.inp, width:120, flex:'none' }} value={task.priority} onChange={e=>updateTask(ti,'priority',e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  <input
                    type="number" min="0"
                    style={{ ...S.inp, width:90, flex:'none' }}
                    value={task.dueDays}
                    onChange={e=>updateTask(ti,'dueDays',e.target.value)}
                    placeholder="Days"
                    title="Due in X days from project start"
                  />
                  <button style={{ ...S.btn('danger'), padding:'6px 10px', flex:'none' }} onClick={()=>removeTaskRow(ti)}><FiTrash2 size={13}/></button>
                </div>

                {/* Checklist items */}
                <div style={{ paddingLeft:8 }}>
                  {(task.checklist||[]).map((ci,cIdx) => (
                    <div key={cIdx} style={{ display:'flex', gap:6, marginBottom:6 }}>
                      <span style={{ color:'#94a3b8', fontSize:13, paddingTop:8 }}>☐</span>
                      <input style={{ ...S.inp, flex:1 }} value={ci.text} onChange={e=>updateCheckItem(ti,cIdx,e.target.value)} placeholder="Checklist item"/>
                      <button style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:'4px 6px' }} onClick={()=>removeCheckItem(ti,cIdx)}><FiX size={14}/></button>
                    </div>
                  ))}
                  <button style={{ ...S.btn(), padding:'5px 12px', fontSize:12, marginTop:2 }} onClick={()=>addCheckItem(ti)}>
                    <FiPlus size={12}/> Add Checklist Item
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
              <button style={S.btn()} onClick={()=>setModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={save} disabled={saving}>
                <FiCheck size={14}/> {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apply Modal ── */}
      {applyModal && (
        <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget)setApplyModal(null)}}>
          <div style={S.smModal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:'#0f172a' }}>Apply Template</h2>
              <button style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }} onClick={()=>setApplyModal(null)}><FiX size={20}/></button>
            </div>
            <p style={{ color:'#64748b', fontSize:13, margin:'0 0 20px' }}>
              <strong>"{applyModal.name}"</strong> — {(applyModal.tasks||[]).length} task{(applyModal.tasks||[]).length!==1?'s':''} will be created.
            </p>

            {/* Project */}
            <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block', marginBottom:6 }}>Select Project *</label>
            <select style={{ ...S.inp, marginBottom:16 }} value={applyProjId} onChange={e=>setApplyProjId(e.target.value)}>
              <option value="">— choose a project —</option>
              {projects.map(p => <option key={p._id||p.id} value={p._id||p.id}>{p.name}</option>)}
            </select>

            {/* Default assignee */}
            <label style={{ fontSize:12, fontWeight:700, color:'#475569', display:'block', marginBottom:6 }}>
              <FiUser size={11} style={{ marginRight:4 }}/>Default Assignee (all tasks)
            </label>
            <select style={{ ...S.inp, marginBottom:20 }} value={defaultAssignee} onChange={e=>setDefaultAssignee(e.target.value)}>
              <option value="">— unassigned —</option>
              {members.map(m => <option key={m._id||m.id} value={m._id||m.id}>{m.name} ({m.position||m.role})</option>)}
            </select>

            {/* Per-task assignees */}
            {(applyModal.tasks||[]).length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#475569', marginBottom:10 }}>
                  Per-Task Assignee Override <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional — overrides default above)</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(applyModal.tasks||[]).map((task, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, background:'#f8fafc', borderRadius:9, padding:'8px 12px', border:'1px solid #f1f5f9' }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:PRIORITY_COLORS[task.priority]||'#94a3b8', flexShrink:0 }}/>
                      <span style={{ flex:1, fontSize:13, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.title}</span>
                      <select
                        style={{ ...S.inp, width:180, flex:'none', fontSize:12, padding:'6px 10px' }}
                        value={taskAssignees[i] || ''}
                        onChange={e => setTaskAssignees(prev => ({ ...prev, [i]: e.target.value }))}
                      >
                        <option value="">— use default —</option>
                        {members.map(m => <option key={m._id||m.id} value={m._id||m.id}>{m.name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={S.btn()} onClick={()=>setApplyModal(null)}>Cancel</button>
              <button style={S.btn('primary')} onClick={applyTemplate} disabled={applying}>
                <FiPlay size={13}/> {applying ? 'Applying…' : 'Apply Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
